import {
  ButtonMediumPrimaryLeadingIcon,
  ButtonSmallSubtle,
  Icon,
} from "@studio3t/generic-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { logsApi } from "@/api/client";
import { JobStatusBadge } from "@/components/JobStatusBadge";
import {
  BreadcrumbCurrent,
  BreadcrumbCurrentGroup,
  BreadcrumbLink,
  PipelineCrumbIcon,
  PipelinesHeader,
} from "@/components/PipelinesHeader";
import { SkeletonBar, SkeletonBlock } from "@/components/Skeleton";
import { useErrorToast } from "@/components/toast/useErrorToast";
import {
  useJobs,
  useRestartJob,
  useServices,
  useStopJob,
} from "@/hooks/useApi";
import { useImportJob } from "@/hooks/useImportJob";
import { deriveJobControlState } from "@/pages/pipelineControls";
import { normalizeState } from "@/pages/pipelineHelpers";
import type { Connector, JobMetadata, LogEntry, Service } from "@/types/api";
import { ConfigurationSection } from "./ConfigurationSection";
import { DownloadReportModal, useReportDownload } from "./DownloadReportModal";
import { sortLogsNewestFirst } from "./logOrdering";
import { MetricsSection } from "./MetricsSection";
import { PipelineFlow } from "./PipelineFlow";
import { PipelineFlowHeader } from "./PipelineFlowHeader";
import { useLiveCheckpoints, useResetOffer } from "./pipelineDetailHooks";
import { ResetPositionModal } from "./ResetPositionModal";
import { SectionTabs } from "./SectionTabs";
import { EmptyState, GridRow, PageSkeletonStack, Section } from "./styles";
import { usePipelineDefinition } from "./usePipelineDefinition";
import { usePipelineFlow } from "./usePipelineFlow";

function findUnresolvedServices(
  pipeline: Connector | null | undefined,
  services: Service[] | null,
): Set<string> | undefined {
  if (!pipeline || !services) return undefined;
  const known = new Set(services.map((s) => s.name));
  const unresolved = new Set(
    stagedServiceNames(pipeline).filter((n) => !known.has(n)),
  );
  return unresolved.size > 0 ? unresolved : undefined;
}

// Every service a pipeline's stages name, in stage order.
function stagedServiceNames(pipeline: Connector): string[] {
  const stages: ({ service_name: string } | null | undefined)[] = [
    pipeline.source,
    ...(pipeline.middlewares ?? []),
    ...pipeline.sinks,
  ];
  return stages.flatMap((stage) => (stage ? [stage.service_name] : []));
}

function useJobControls(name: string | undefined) {
  const { mutateAsync: stopJob, isPending: stopping } = useStopJob();
  const { mutateAsync: restartJob, isPending: restarting } = useRestartJob();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // useStopJob/useRestartJob invalidate the jobs query on settle and useJobs
  // polls, so the list converges without a manual refetch here.
  const run = useCallback(
    async (action: (name: string) => Promise<unknown>, fallback: string) => {
      if (!name) return;
      try {
        await action(name);
      } catch (err) {
        setErrorMessage(err instanceof Error ? err.message : fallback);
        setTimeout(() => setErrorMessage(null), 5000);
      }
    },
    [name],
  );

  const handleStop = useCallback(
    () => run(stopJob, "Failed to stop pipeline"),
    [run, stopJob],
  );

  const handleRestart = useCallback(
    () => run(restartJob, "Failed to start pipeline"),
    [run, restartJob],
  );

  const dismissError = useCallback(() => setErrorMessage(null), []);

  return {
    handleStop,
    handleRestart,
    stopping,
    restarting,
    errorMessage,
    dismissError,
  };
}

function useRecentLogs(name: string | undefined) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!name) return;
    setLoading(true);
    logsApi
      .get({ job_name: name, limit: 20 })
      .then((response) => setLogs(sortLogsNewestFirst(response.logs)))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [name]);

  return { logs, loading };
}

function DetailErrorToast({
  message,
  fallback,
  onDismiss,
}: {
  message: string | null | undefined;
  fallback: string | null | undefined;
  onDismiss: () => void;
}) {
  useErrorToast(message ?? fallback, onDismiss);
  return null;
}

// Shown while the jobs query loads: blocks approximate the real layout
// (breadcrumb row, flow card, metrics/config grid, tabbed section) so the page
// doesn't jump when the content arrives.
function PageSkeleton() {
  return (
    <PageSkeletonStack aria-hidden="true">
      <SkeletonBar $width="280px" $height="20px" />
      <Section>
        <SkeletonBlock $height="220px" />
      </Section>
      <GridRow>
        <SkeletonBlock $height="240px" />
        <SkeletonBlock $height="240px" />
      </GridRow>
      <Section>
        <SkeletonBlock $height="200px" />
      </Section>
    </PageSkeletonStack>
  );
}

// The page's two dialogs: resetting the position and downloading a report.
function PipelineDialogs({
  job,
  reset,
  report,
}: {
  job: JobMetadata;
  reset: ReturnType<typeof useResetOffer>;
  report: ReturnType<typeof useReportDownload>;
}) {
  return (
    <>
      <ResetPositionModal
        isOpen={reset.open}
        job={job.name}
        onClose={reset.close}
      />
      <DownloadReportModal
        isOpen={report.open}
        pipeline={job.name}
        onDownload={report.download}
        onClose={() => report.setOpen(false)}
      />
    </>
  );
}

function PipelineDetail() {
  const { name } = useParams<{ name: string }>();
  const jobName = name ?? "";
  const navigate = useNavigate();

  const { data: jobs, isLoading: jobsLoading } = useJobs();
  const { data: services } = useServices();
  const controls = useJobControls(name);
  const {
    importJob,
    loading: importing,
    error: importError,
    dismissError: dismissImportError,
  } = useImportJob();
  const recentLogs = useRecentLogs(name);
  const report = useReportDownload(jobName);

  const job = useMemo(() => jobs?.find((j) => j.name === name), [jobs, name]);
  const live = useLiveCheckpoints(jobName, job);
  const reset = useResetOffer(job, services);
  const definition = usePipelineDefinition(jobName, job?.pipeline);

  const unresolvedServices = useMemo(
    () => findUnresolvedServices(job?.pipeline, services ?? null),
    [job?.pipeline, services],
  );

  const flow = usePipelineFlow(jobName, job);

  const handleBack = useCallback(() => {
    navigate("/pipelines");
  }, [navigate]);

  if (jobsLoading && !jobs) {
    return <PageSkeleton />;
  }

  if (!job) {
    return (
      <EmptyState>
        <p>Pipeline "{name}" not found</p>
        <ButtonSmallSubtle text="Back to Pipelines" onClick={handleBack} />
      </EmptyState>
    );
  }

  const state = normalizeState(job.state);
  const desiredState = normalizeState(job.desired_state);
  // While a start/stop is converging (state lags desired_state), show the
  // transitional badge so the UI matches the disabled buttons.
  const { displayState } = deriveJobControlState(state, desiredState);
  const isRunning = state === "Running";
  const pipeline = job.pipeline;

  return (
    <div>
      <PipelinesHeader
        count={jobs?.length}
        // A single pipeline's title-row action is its processing report
        // (TTTL-220) in place of the list's Add pipeline, on the same
        // primary button (Figma Bridge_PageTitle 14899-161255); the modal
        // picks the format and moment.
        action={
          <ButtonMediumPrimaryLeadingIcon
            text="Download report"
            icon={Icon.Export}
            onClick={() => report.setOpen(true)}
          />
        }
        breadcrumb={
          <>
            <BreadcrumbLink text="Pipelines list" onClick={handleBack} />
            <BreadcrumbCurrentGroup>
              <BreadcrumbCurrent>
                <PipelineCrumbIcon muted />
                {job.name}
              </BreadcrumbCurrent>
              <JobStatusBadge state={displayState} />
            </BreadcrumbCurrentGroup>
          </>
        }
      />

      <PipelineFlow
        pipeline={pipeline}
        unresolvedServices={unresolvedServices}
        flow={flow}
        header={
          <PipelineFlowHeader
            name={job.name}
            state={state}
            desiredState={desiredState}
            onStart={controls.handleRestart}
            onStop={controls.handleStop}
            onOpenInStudio={() => importJob(job)}
            starting={controls.restarting}
            stopping={controls.stopping}
            importing={importing}
            reset={reset.offer}
          />
        }
      />

      <PipelineDialogs job={job} reset={reset} report={report} />

      <GridRow>
        <MetricsSection jobName={jobName} isRunning={isRunning} />
        <ConfigurationSection
          job={job}
          pipeline={pipeline}
          workspace={definition.workspace}
        />
      </GridRow>

      <SectionTabs
        jobName={jobName}
        isRunning={isRunning}
        scripts={definition.scripts}
        checkpoints={live.checkpoints}
        // A fetch in flight is not "no checkpoints" — the tab shows a skeleton
        // until the query resolves.
        checkpointsLoading={live.loading}
        logs={recentLogs.logs}
        logsLoading={recentLogs.loading}
      />

      <DetailErrorToast
        message={controls.errorMessage}
        fallback={importError}
        onDismiss={() => {
          controls.dismissError();
          dismissImportError();
        }}
      />
    </div>
  );
}

export default PipelineDetail;
